import React, { useState, useRef, useEffect } from "react";
import jsQR from "jsqr";

const QRScanner = ({ onScan, employees }) => {
  const [scanStatus, setScanStatus] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [stream, setStream] = useState(null);
  const [location, setLocation] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("buscando"); // buscando, listo, error

  const isScanningRef = useRef(false);
  const lastScanAtRef = useRef(0);

  useEffect(() => {
    let watchId;
    if ("geolocation" in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setGpsStatus("listo");
        },
        (error) => {
          console.warn("GPS no disponible o permiso denegado", error);
          setGpsStatus("error");
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setGpsStatus("error");
    }
    
    return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  const startScanner = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      videoRef.current.srcObject = mediaStream;
      setStream(mediaStream);
      setIsScanning(true);
      isScanningRef.current = true;
      videoRef.current.play();
      requestAnimationFrame(tick);
    } catch (err) {
      console.error("Error al acceder a la cámara: ", err);
      setScanStatus("error-camera");
      setIsScanning(false);
      isScanningRef.current = false;
    }
  };

  const stopScanner = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsScanning(false);
    isScanningRef.current = false;
  };

  const tick = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const now = Date.now();
      const canDecode = now - lastScanAtRef.current > 1200;
      if (canDecode) {
        try {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: "dontInvert",
          });

          if (code?.data) {
            lastScanAtRef.current = now;
            const employee = employees.find((emp) => emp.qrCode === code.data);
            if (employee) {
              setScanStatus("success");
              onScan(employee, location);
              stopScanner();
              setTimeout(() => setScanStatus(""), 2000);
              return;
            } else {
              setScanStatus("error");
              setTimeout(() => setScanStatus(""), 2000);
            }
          }
        } catch (err) {
          console.error("Error decodificando QR:", err);
        }
      }
    }
    if (isScanningRef.current) requestAnimationFrame(tick);
  };

  // Simulación de entrada de QR (para pruebas sin lector real)
  const handleManualQRInput = (e) => {
    const data = e.target.value.trim();
    if (data.length > 0) {
      const employee = employees.find((emp) => emp.qrCode === data);
      if (employee) {
        setScanStatus("success");
        onScan(employee, location);
        setTimeout(() => {
          setScanStatus("");
          e.target.value = ""; // Limpiar campo
        }, 2000);
      } else {
        setScanStatus("error");
      }
    }
  };

  useEffect(() => {
    return () => {
      stopScanner(); // Detener la cámara al desmontar el componente
    };
  }, []);

  return (
    <div className="w-full max-w-2xl mx-auto p-8 glass-card rounded-2xl shadow-2xl border-slate-800/80">
      <h2 className="text-2xl font-bold mb-2 text-slate-100 bg-clip-text text-transparent bg-gradient-to-r from-slate-100 to-slate-300">
        Escáner de Asistencia
      </h2>
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-slate-400">
          Coloque el código QR frente a la cámara.
        </p>
        <div className={`px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1.5 ${
          gpsStatus === 'listo' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 
          gpsStatus === 'buscando' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 
          'bg-rose-500/10 text-rose-400 border-rose-500/30'
        }`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {gpsStatus === 'listo' ? 'GPS Activo' : gpsStatus === 'buscando' ? 'Buscando GPS...' : 'Sin GPS'}
        </div>
      </div>

      <div className="mb-6 flex flex-col items-center">
        <div className="relative w-full max-w-md h-72 rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 flex items-center justify-center shadow-2xl shadow-black/80">
          <video
            ref={videoRef}
            className={`w-full h-full object-cover ${!isScanning ? 'hidden' : ''}`}
          ></video>
          {isScanning ? (
            <>
              {/* High-tech overlay HUD */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Laser animation */}
                <div className="absolute left-0 right-0 h-0.5 bg-emerald-400 shadow-[0_0_12px_#34d399] animate-scanner-laser"></div>
                
                {/* Corner guidelines */}
                <div className="absolute top-4 left-4 w-6 h-6 border-t-2 border-l-2 border-emerald-400"></div>
                <div className="absolute top-4 right-4 w-6 h-6 border-t-2 border-r-2 border-emerald-400"></div>
                <div className="absolute bottom-4 left-4 w-6 h-6 border-b-2 border-l-2 border-emerald-400"></div>
                <div className="absolute bottom-4 right-4 w-6 h-6 border-b-2 border-r-2 border-emerald-400"></div>
                
                {/* Glow ring in middle */}
                <div className="absolute inset-12 border border-emerald-500/10 rounded-xl animate-pulse"></div>
              </div>
            </>
          ) : (
            <div className="text-center p-6 space-y-4">
              <div className="w-16 h-16 bg-slate-900 border border-slate-800 rounded-full flex items-center justify-center mx-auto text-slate-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-slate-500 text-sm">Cámara actualmente inactiva</p>
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
        </div>

        {/* Action Button */}
        <div className="w-full max-w-md flex justify-center">
          {!isScanning ? (
            <button
              onClick={startScanner}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/10 hover:shadow-emerald-500/20 hover:-translate-y-0.5 transition-all duration-200"
            >
              Iniciar Escáner de Cámara
            </button>
          ) : (
            <button
              onClick={stopScanner}
              className="w-full py-3 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 text-white font-semibold rounded-xl shadow-lg shadow-rose-500/10 hover:shadow-rose-500/20 hover:-translate-y-0.5 transition-all duration-200"
            >
              Detener Escáner de Cámara
            </button>
          )}
        </div>
      </div>

      {/* Notifications / Alerts */}
      <div className="space-y-3 w-full max-w-md mx-auto mb-8">
        {scanStatus === "success" && (
          <div className="p-4 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/25 flex items-center gap-3 animate-fade-in-up">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium">Asistencia registrada correctamente.</span>
          </div>
        )}
        {scanStatus === "error" && (
          <div className="p-4 bg-rose-500/10 text-rose-400 rounded-xl border border-rose-500/25 flex items-center gap-3 animate-pulse">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium">Código QR no registrado o inválido.</span>
          </div>
        )}
        {scanStatus === "error-camera" && (
          <div className="p-4 bg-amber-500/10 text-amber-400 rounded-xl border border-amber-500/25 flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm font-medium">Error de acceso a cámara. Verifique los permisos de su navegador.</span>
          </div>
        )}
      </div>

      {/* Manual input simulation */}
      <div className="bg-slate-950/60 border border-slate-900 p-6 rounded-2xl w-full">
        <h3 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          Ingreso Manual de Credencial
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Si el lector de cámara no está disponible, ingrese el código de credencial del empleado (ej. EMP-DNI-ID):
        </p>
        <input
          type="text"
          placeholder="Ingrese código QR o DNI..."
          onBlur={handleManualQRInput}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleManualQRInput(e);
          }}
          className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/25 transition-all duration-200"
        />
      </div>
    </div>
  );
};

export default QRScanner;
